import { formatMultiLanguageField } from 'lib/utils'
import { NutrientDetail, NutrientHeader } from 'types/api/product'
import { v4 as getRandomId } from 'uuid'

const nutrientCodes = {
  energy: 'ENER-',
  fat: 'FAT',
  saturatedFat: 'FASAT',
  carbohydrate: 'CHO-',
  sugar: 'SUGAR',
  fiber: 'FIB-',
  salt: 'NACL',
  protein: 'PRO-',
}

type OptionalNutrient = {
  dailyValueIntakePercent: number | string
  nutrientTypeCode: string
  id: string
  quantity?: number
  quantityUnit?: string
}

const findNutrient = (nutrientDetails: NutrientDetail[], code: string) => {
  // alter original nutrientDetail and remove item if it is found
  const index = nutrientDetails.findIndex(
    (item) => item.nutrientTypeCode === code
  )
  const foundNutrient =
    index !== -1 ? nutrientDetails.splice(index, index + 1)?.pop() : null
  if (!foundNutrient) {
    return {
      nutrientTypeCode: code,
      dailyValueIntakePercent: '',
      ...(code === nutrientCodes.energy
        ? { kjQuantity: '', kCalQuantity: '' }
        : { quantity: '' }),
    }
  }

  const baseProperties = {
    dailyValueIntakePercent: foundNutrient.dailyValueIntakePercent ?? '',
    nutrientTypeCode: foundNutrient.nutrientTypeCode,
  }
  if (code === nutrientCodes.energy) {
    const kjQuantityContained =
      foundNutrient.quantityContained?.length > 0
        ? foundNutrient.quantityContained?.find(
            (quantity: {
              $: number | undefined
              '@measurementUnitCode': string
            }) => quantity['@measurementUnitCode'] === 'KJO'
          )
        : null
    const kCalQuantityContained =
      foundNutrient.quantityContained?.length > 0
        ? foundNutrient.quantityContained?.find(
            (quantity: {
              $: number | undefined
              '@measurementUnitCode': string
            }) => quantity['@measurementUnitCode'] === 'E14'
          )
        : null
    return {
      ...baseProperties,
      kjQuantity: kjQuantityContained?.$ ?? '',
      kCalQuantity: kCalQuantityContained?.$ ?? '',
    }
  }

  return {
    ...baseProperties,
    quantity: foundNutrient.quantityContained?.[0]?.$ ?? '',
    quantityUnit:
      foundNutrient.quantityContained?.[0]?.['@measurementUnitCode'],
  }
}

export const useNutrientHeader = (nutrientHeaders: NutrientHeader[]) => {
  const tempNutrientHeaders = nutrientHeaders
    ? [...nutrientHeaders]
    : [
        {
          id: getRandomId(),
          nutrientDetails: [],
        },
      ]

  return tempNutrientHeaders?.map((nutrientHeader: Partial<NutrientHeader>) => {
    const nutrientBasisQuantity =
      nutrientHeader.nutrientBasisQuantity?.$ || undefined
    const nutrientBasisQuantityCode =
      nutrientHeader.nutrientBasisQuantity?.['@measurementUnitCode'] || null

    const servingSizeDescription = formatMultiLanguageField(
      nutrientHeader.servingSizeDescription
    )
    const dailyValueIntakeReference = formatMultiLanguageField(
      nutrientHeader.dailyValueIntakeReference
    )

    const nutrientDetail = nutrientHeader.nutrientDetail
      ? [...nutrientHeader.nutrientDetail]
      : []
    // find mandatory nutrient fields
    const energy = findNutrient(nutrientDetail, nutrientCodes.energy)
    const fat = findNutrient(nutrientDetail, nutrientCodes.fat)
    const saturatedFat = findNutrient(
      nutrientDetail,
      nutrientCodes.saturatedFat
    )
    const carbohydrate = findNutrient(
      nutrientDetail,
      nutrientCodes.carbohydrate
    )
    const sugar = findNutrient(nutrientDetail, nutrientCodes.sugar)
    const fiber = findNutrient(nutrientDetail, nutrientCodes.fiber)
    const salt = findNutrient(nutrientDetail, nutrientCodes.salt)
    const protein = findNutrient(nutrientDetail, nutrientCodes.protein)

    // for optional fields, separate nutrients if there is more than one quantity contained
    const optionalNutrientDetails = nutrientDetail.reduce((acc, detail) => {
      const nutrientBase = {
        dailyValueIntakePercent: detail.dailyValueIntakePercent ?? '',
        nutrientTypeCode: detail.nutrientTypeCode,
        id: getRandomId(),
      }
      if (!detail.quantityContained) {
        acc.push(nutrientBase)
        return acc
      }
      const newAcc = [] as OptionalNutrient[]
      detail.quantityContained.forEach((quantity) => {
        newAcc.push({
          quantity: quantity.$,
          quantityUnit: quantity['@measurementUnitCode'],
          ...nutrientBase,
          id: getRandomId(),
        })
      })
      return [...acc, ...newAcc]
    }, [] as OptionalNutrient[])

    return {
      nutrientBasisQuantity,
      nutrientBasisQuantityCode,
      servingSizeDescription,
      id: nutrientHeader.id || getRandomId(),
      dailyValueIntakeReference,
      nutrientDetails: optionalNutrientDetails,
      mandatoryNutrientDetails: {
        energy,
        fat,
        saturatedFat,
        carbohydrate,
        sugar,
        fiber,
        salt,
        protein,
      },
    }
  })
}
